import Microgame from '../engine/Microgame.js';

export default class StackGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Empile !`, en: `Stack!`, es: `¡Apila!` },
            controls: { fr: `Appuie au bon moment pour lâcher le bloc`, en: `Tap at the right time to drop the block`, es: `Toca en el momento adecuado para soltar el bloque` },
            objective: { fr: `Monte la tour le plus haut possible sans qu'elle tombe`, en: `Build the tower as high as possible`, es: `Construye la torre lo más alto posible sin que caiga` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><rect x="6" y="16" width="12" height="6"/><rect x="8" y="10" width="8" height="6"/><rect x="10" y="4" width="4" height="6"/></svg>`,
            actions: [
                { type: 'wait', duration: 520 },
                { type: 'tap', x: 50, y: 35, duration: 160 },
                { type: 'wait', duration: 520 },
                { type: 'tap', x: 50, y: 30, duration: 160 },
                { type: 'wait', duration: 520 },
                { type: 'tap', x: 50, y: 25, duration: 160 },
                { type: 'wait', duration: 380 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.stack = [];
        this.currentBlock = null;
        this.direction = 1;
        this.baseY = 0;
        this.targetStackSize = 3;
        this.gameWon = false;
        this.blockHeight = 40;

        const isInfinite = this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack));

        if (!isInfinite) {
            const sm = this.engine.speedMultiplier || 1.0;
            // Width shrinks as it gets faster (150 -> ~75 at x4)
            this.blockWidth = Math.max(75, 150 - (sm - 1) * 25);
            // Speed drastically increases so blocks can reach the center 3 times within the time limit
            this.speed = 350 + (sm - 1) * 205; // x1 = 350, x4 = 965 px/s
            // Base duration increased so 3 human reactions are mathematically possible at x4 speed (1125ms total)
            this.duration = 4500;
        } else {
            // Keep infinite mode logic exactly as it was
            this.speed = 300;
            this.blockWidth = 150;
        }
    }

    setup() {
        this.engine.ui.instruction.textContent = "STACK!";
        this.baseY = this.container.clientHeight - 50;

        // Base platform
        const base = document.createElement('div');
        base.style.position = 'absolute';
        base.style.bottom = '0';
        base.style.left = '50%';
        base.style.transform = 'translateX(-50%)';
        base.style.width = '200px';
        base.style.height = '50px';
        base.style.background = '#333';
        base.style.borderTop = '2px solid #ccff00';
        this.container.appendChild(base);

        this.spawnBlock();

        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);
    }

    spawnBlock() {
        const isInfinite = this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack));

        if (!isInfinite && this.stack.length >= this.targetStackSize) {
            this.win();
            this.gameWon = true;
            return;
        }

        // Infinite Mode: Reset stack if too high to keep playing
        if (isInfinite && this.stack.length >= 8) {
            // Difficulty Up! Aggressive scaling.
            this.blockWidth = Math.max(30, this.blockWidth * 0.8); // Shrink 20%
            this.speed *= 1.25; // Speed up 25%

            // Visual clear
            this.stack.forEach(el => {
                el.style.transition = 'top 0.5s, opacity 0.5s';
                el.style.top = '100%';
                el.style.opacity = '0';
                setTimeout(() => el.remove(), 500);
            });
            this.stack = [];

            // Bonus or visual feedback
            const bonus = document.createElement('div');
            bonus.textContent = "LEVEL UP! FASTER!";
            bonus.style.position = 'absolute';
            bonus.style.top = '50%';
            bonus.style.left = '50%';
            bonus.style.transform = 'translate(-50%, -50%)';
            bonus.style.color = '#ccff00'; // Neon Green
            bonus.style.fontSize = '2.5rem';
            bonus.style.fontWeight = 'bold';
            bonus.style.textShadow = '0 0 20px #ccff00';
            bonus.style.zIndex = '100';
            this.container.appendChild(bonus);
            setTimeout(() => bonus.remove(), 1000);
        }

        const block = document.createElement('div');
        block.style.position = 'absolute';
        block.style.width = `${this.blockWidth}px`;
        block.style.height = `${this.blockHeight}px`;
        block.style.background = this.secondaryColor; // Spawn as Secondary
        block.style.boxShadow = `0 0 10px ${this.secondaryColor}`;

        // Start position (left or right)
        this.direction = Math.random() > 0.5 ? 1 : -1;
        const startX = this.direction === 1 ? 0 : this.container.clientWidth - this.blockWidth;

        block.style.left = `${startX}px`;
        block.style.bottom = `${50 + this.stack.length * this.blockHeight}px`;

        this.container.appendChild(block);
        this.currentBlock = {
            element: block,
            x: startX
        };
    }

    onTap() {
        if (!this.active || this.gameWon || !this.currentBlock) return;

        // Stop the block
        const blockRect = this.currentBlock.element.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();

        // Calculate center positions relative to container
        const blockCenter = (blockRect.left + blockRect.width / 2) - containerRect.left;
        const prevCenter = this.stack.length > 0
            ? (this.stack[this.stack.length - 1].getBoundingClientRect().left + this.stack[this.stack.length - 1].getBoundingClientRect().width / 2) - containerRect.left
            : containerRect.width / 2; // Base center

        const diff = Math.abs(blockCenter - prevCenter);
        const maxDiff = (this.blockWidth / 2) + 20; // Forgiveness

        if (diff > maxDiff) {
            // Missed
            this.currentBlock.element.style.background = '#ff0055';
            this.currentBlock.element.style.boxShadow = '0 0 10px #ff0055';
            this.lose();
        } else {
            // Success
            this.currentBlock.element.style.background = this.primaryColor; // Become Primary
            this.currentBlock.element.style.boxShadow = `0 0 10px ${this.primaryColor}`;
            this.stack.push(this.currentBlock.element);
            this.currentBlock = null;

            // Score for Time Attack / Infinite
            const isInfinite = this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack));
            if (isInfinite) {
                this.engine.score++;
                this.engine.updateHUD(); // If needed
            }

            // Make next block smaller? Optional difficulty increase
            // this.blockWidth *= 0.9; 

            this.spawnBlock();
        }
    }

    update(dt) {
        if (this.currentBlock && !this.gameWon) {
            let newX = this.currentBlock.x + (this.speed * (dt / 1000) * this.direction);

            // Bounce
            if (newX <= 0) {
                newX = 0;
                this.direction = 1;
            } else if (newX + this.blockWidth >= this.container.clientWidth) {
                newX = this.container.clientWidth - this.blockWidth;
                this.direction = -1;
            }

            this.currentBlock.x = newX;
            this.currentBlock.element.style.left = `${newX}px`;
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
